import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { createUser } from 'actions/userActions';
import { trackCustomEvent } from 'utilities/app-insights';
import Env from 'Env';

/**
 * Custom hook to manage the add user workflow state and actions.
 * Handles step navigation, form submissions, and user creation.
 *
 * @param {Object} params - Workflow parameters
 * @param {Object} params.steps - Step enum constants
 * @param {string} params.email - User email
 * @param {Array} params.assignRolesList - Available roles list
 * @param {string} params.assignedGrandfatherClientNumber - Grandfather client number
 * @param {Object} params.loggedInUser - Currently logged in user
 * @param {string} params.assignedAccountNumber - Assigned account number
 * @param {Function} params.setFormId - Function to update form ID
 * @param {string} params.addUserDetailsFormId - Form ID for user details
 * @param {string} params.adminAddUserAssignAccountsFormId - Form ID for admin account assignment
 * @param {Function} params.onClose - Modal close callback
 *
 * @returns {Object} Workflow state and handlers
 * @returns {number} stepId - Current step ID
 * @returns {Object|null} userDetails - User details data
 * @returns {string|null} roleId - Selected role ID
 * @returns {Function} handleAddUserDetailsSubmit - Handler for user details submission
 * @returns {Function} handleCustomerAddUser - Handler for customer portal user creation
 * @returns {Function} handleAdminAssignAccountsSubmit - Handler for admin portal user creation
 * @returns {Function} getRoleIdByName - Utility to get role ID from role name
 * @returns {Function} closeModal - Handler for closing the modal
 * @returns {Function} setStepId - Function to update current step
 */
export const useAddUserFlow = ({
  steps,
  email,
  assignRolesList,
  assignedGrandfatherClientNumber,
  loggedInUser,
  assignedAccountNumber,
  setFormId,
  adminAddUserAssignAccountsFormId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const isAdminPortal = Env.isAdminPortal();

  const [stepId, setStepId] = useState(steps.EmailLookup);
  const [userDetails, setUserDetails] = useState(null);
  const [roleId, setRoleId] = useState(null);

  const getRoleIdByName = useCallback(
    (roleName) => {
      const role = assignRolesList.find((r) => r.name === roleName);
      return typeof role !== 'undefined' ? role.id : '';
    },
    [assignRolesList]
  );

  const addUser = useCallback(
    (values) => {
      dispatch(
        createUser(
          isAdminPortal ? values.grandParentCustomerNumber : assignedGrandfatherClientNumber,
          values.firstName,
          values.lastName,
          email,
          values.roleId,
          values.assignedAccounts
        )
      );

      trackCustomEvent(
        'Submit Add New User',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to submit Add New User. '
      );
    },
    [dispatch, isAdminPortal, assignedGrandfatherClientNumber, email, loggedInUser, assignedAccountNumber]
  );

  const handleAddUserDetailsSubmit = useCallback(
    (values) => {
      setUserDetails({ ...values });
      setFormId(isAdminPortal ? adminAddUserAssignAccountsFormId : 'customer-portal-assign-accounts-form');
      setStepId(isAdminPortal ? steps.AdminAssignAccounts : steps.CustomerPortalAssignAccounts);
    },
    [isAdminPortal, adminAddUserAssignAccountsFormId, setFormId, steps, setStepId]
  );

  const handleCustomerAddUser = useCallback(
    (assignedAccounts, roleName) => {
      const user = {
        ...userDetails,
        roleId: getRoleIdByName(roleName),
        assignedAccounts: assignedAccounts,
      };
      setRoleId(getRoleIdByName(roleName));
      addUser(user);
    },
    [userDetails, getRoleIdByName, addUser]
  );

  const handleAdminAssignAccountsSubmit = useCallback(
    (values) => {
      const user = {
        ...userDetails,
        grandParentCustomerNumber: values.grandParentCustomerNumber,
        assignedAccounts: values.assignedAccounts,
        roleId: values.roleId,
      };
      setRoleId(values.roleId);
      addUser(user);
    },
    [userDetails, addUser]
  );

  const closeModal = useCallback(() => {
    trackCustomEvent('Close Add New User', loggedInUser, assignedAccountNumber, 'Add New User Modal closed.');
    onClose && onClose();
  }, [loggedInUser, assignedAccountNumber, onClose]);

  const resetUserDetails = useCallback(() => {
    setUserDetails(null);
  }, []);

  return {
    stepId,
    setStepId,
    userDetails,
    roleId,
    handleAddUserDetailsSubmit,
    handleCustomerAddUser,
    handleAdminAssignAccountsSubmit,
    getRoleIdByName,
    closeModal,
    resetUserDetails,
  };
};
